import React, { useEffect, useState } from 'react';
import { sp } from './sponsorApi';
import { BarChart3, Plus, Send, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';

const STATUS_META = {
  DRAFT: { l: 'Brouillon', c: 'bg-slate-700 text-slate-300', icon: Clock },
  SUBMITTED: { l: 'Soumis', c: 'bg-blue-600/30 text-blue-300', icon: Send },
  VALIDATED: { l: 'Validé O\'SCOP', c: 'bg-emerald-600/30 text-emerald-300', icon: CheckCircle2 },
  REJECTED: { l: 'Rejeté', c: 'bg-red-600/30 text-red-300', icon: XCircle },
};

export default function ImpactTab() {
  const [indicators, setIndicators] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ind, setInd] = useState({ name: '', unit: '', target: '' });
  const [meas, setMeas] = useState({ indicator_id: '', value: '', period: '' });
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await sp.get('/impact');
      setIndicators(r.data.indicators || []);
      setMeasurements(r.data.measurements || []);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addIndicator = async () => {
    if (!ind.name || !ind.unit) return;
    await sp.post('/impact/indicateurs', { name: ind.name, unit: ind.unit, target: parseFloat(ind.target) || null });
    setInd({ name: '', unit: '', target: '' }); await load();
  };
  const addMeasurement = async () => {
    setMsg('');
    if (!meas.indicator_id || !meas.period) { setMsg('Choisissez un indicateur et une période.'); return; }
    try {
      await sp.post('/impact/mesures', { indicator_id: meas.indicator_id, value: parseFloat(meas.value) || 0, period: meas.period });
      setMeas({ indicator_id: '', value: '', period: '' }); await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Échec.'); }
  };
  const submit = async (id) => { await sp.post(`/impact/mesures/${id}/soumettre`, {}); await load(); };

  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="impact-tab">
      <div className="flex items-start gap-3 text-slate-300 bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 text-sm">
        <BarChart3 className="w-5 h-5 text-emerald-400 mt-0.5" />
        <p>Seules les mesures <b className="text-emerald-300">validées par O'SCOP</b> et marquées publiables alimentent les vues publiques d'impact.</p>
      </div>

      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h2 className="text-white font-semibold mb-4">Indicateurs d'impact</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <input value={ind.name} onChange={(e) => setInd({ ...ind, name: e.target.value })} placeholder="Nom (ex: Emplois créés)" data-testid="ind-name-input" className="md:col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <input value={ind.unit} onChange={(e) => setInd({ ...ind, unit: e.target.value })} placeholder="Unité (ETP, €, …)" data-testid="ind-unit-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <button onClick={addIndicator} disabled={!ind.name || !ind.unit} data-testid="ind-add-btn" className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center justify-center gap-1"><Plus className="w-4 h-4" />Créer</button>
        </div>
        <ul className="flex flex-wrap gap-2">
          {indicators.length === 0 && <li className="text-slate-500 text-sm" data-testid="ind-empty">Aucun indicateur.</li>}
          {indicators.map((i) => (<li key={i.id} className="bg-slate-800/60 rounded-lg px-3 py-1.5 text-sm text-slate-200" data-testid={`ind-${i.id}`}>{i.name} <span className="text-slate-500 text-xs">({i.unit})</span></li>))}
        </ul>
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h2 className="text-white font-semibold mb-4">Mesures</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <select value={meas.indicator_id} onChange={(e) => setMeas({ ...meas, indicator_id: e.target.value })} data-testid="meas-indicator-select" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm">
            <option value="">Indicateur…</option>
            {indicators.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <input type="number" value={meas.value} onChange={(e) => setMeas({ ...meas, value: e.target.value })} placeholder="Valeur" data-testid="meas-value-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <input value={meas.period} onChange={(e) => setMeas({ ...meas, period: e.target.value })} placeholder="Période (ex: 2025-T4)" data-testid="meas-period-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <button onClick={addMeasurement} data-testid="meas-add-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center justify-center gap-1"><Plus className="w-4 h-4" />Ajouter</button>
        </div>
        {msg && <p className="text-sm text-amber-300 mb-2" data-testid="meas-msg">{msg}</p>}
        <ul className="space-y-2">
          {measurements.length === 0 && <li className="text-slate-500 text-sm" data-testid="meas-empty">Aucune mesure.</li>}
          {measurements.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.DRAFT;
            const Icon = meta.icon;
            return (
              <li key={m.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2 text-sm" data-testid={`meas-${m.id}`}>
                <div>
                  <span className="text-white">{m.indicator_name} : {m.value} {m.unit}</span>
                  <span className="text-slate-500 ml-2 text-xs">{m.period}</span>
                  {m.is_public && <span className="ml-2 text-xs text-emerald-400">· public</span>}
                  {m.review_comment && <p className="text-slate-500 text-xs mt-0.5">« {m.review_comment} »</p>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${meta.c}`}><Icon className="w-3 h-3" />{meta.l}</span>
                  {(m.status === 'DRAFT' || m.status === 'REJECTED') && (
                    <button onClick={() => submit(m.id)} data-testid={`meas-submit-${m.id}`} className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"><Send className="w-3 h-3" />Soumettre</button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
